import { useState } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Stack, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Tooltip, Alert,
} from '@mui/material';
import { Add, PlayArrow, Pause, NetworkCheck, Delete, VpnKey, Token, Terminal } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import {
  integrationApi, oauthApi, type Integration, type CreateIntegrationInput, type ConnectorMeta, type SecretKind,
} from '../../api/integration.api';

const CATEGORIES = ['crm', 'oms', 'erp', 'logistics', 'sap', 'tally', 'oem', 'cpo', 'custom'] as const;
const DIRECTIONS = ['inbound', 'outbound', 'bidirectional'] as const;
const AUTH_TYPES = ['none', 'api_key', 'oauth2', 'hmac', 'basic'] as const;

const statusColor: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  active: 'success', draft: 'default', paused: 'warning', error: 'error', disabled: 'default',
};
const healthColor: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  healthy: 'success', degraded: 'warning', down: 'error', unknown: 'default',
};

export function IntegrationsPage() {
  const { hasPermission } = useAuth();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [credFor, setCredFor] = useState<Integration | null>(null);
  const [oauthFor, setOauthFor] = useState<Integration | null>(null);
  const [runFor, setRunFor] = useState<Integration | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  const { data } = useQuery({ queryKey: ['integrations'], queryFn: () => integrationApi.list({ limit: 100 }) });
  const { data: connectors } = useQuery({ queryKey: ['integration-marketplace'], queryFn: () => integrationApi.marketplace() });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['integrations'] });
  const activate = useMutation({ mutationFn: (id: number) => integrationApi.activate(id), onSuccess: invalidate });
  const pause = useMutation({ mutationFn: (id: number) => integrationApi.pause(id), onSuccess: invalidate });
  const remove = useMutation({ mutationFn: (id: number) => integrationApi.remove(id), onSuccess: invalidate });
  const test = useMutation({
    mutationFn: (id: number) => integrationApi.test(id),
    onSuccess: (r) => { setTestMsg(`${r.ok ? '✓' : '✗'} ${r.message}${r.simulated ? ' (simulated)' : ''} — ${r.durationMs}ms`); invalidate(); },
  });

  const rows = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Integrations"
        subtitle="Configure connections to client CRM/OMS/ERP, SAP, Tally, logistics and OEM/CPO platforms."
        action={hasPermission('integration.create') && <Button startIcon={<Add />} variant="contained" onClick={() => setCreateOpen(true)}>New Integration</Button>}
      />
      {testMsg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setTestMsg(null)}>{testMsg}</Alert>}

      <Card>
        <CardContent sx={{ p: 0, overflowX: 'auto' }}>
          {rows.length === 0 ? <EmptyState title="No integrations yet" description="Create one from a marketplace connector." dense /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell><TableCell>Name</TableCell><TableCell>Connector</TableCell>
                  <TableCell>Category</TableCell><TableCell>Direction</TableCell><TableCell>Auth</TableCell>
                  <TableCell>Status</TableCell><TableCell>Health</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.code}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.connector_key}</TableCell>
                    <TableCell sx={{ textTransform: 'uppercase' }}>{r.category}</TableCell>
                    <TableCell>{r.direction}</TableCell>
                    <TableCell>{r.auth_type}</TableCell>
                    <TableCell><Chip size="small" color={statusColor[r.status]} label={r.status} /></TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={healthColor[r.health_status]} label={r.health_status} /></TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {hasPermission('integration.test') && <Tooltip title="Test connection"><IconButton size="small" onClick={() => test.mutate(r.id)}><NetworkCheck fontSize="small" /></IconButton></Tooltip>}
                        {hasPermission('integration.manage') && <Tooltip title="Credentials"><IconButton size="small" onClick={() => setCredFor(r)}><VpnKey fontSize="small" /></IconButton></Tooltip>}
                        {r.auth_type === 'oauth2' && hasPermission('integration.manage') && <Tooltip title="OAuth2"><IconButton size="small" onClick={() => setOauthFor(r)}><Token fontSize="small" /></IconButton></Tooltip>}
                        {hasPermission('integration.manage') && <Tooltip title="Run / Import"><IconButton size="small" onClick={() => setRunFor(r)}><Terminal fontSize="small" /></IconButton></Tooltip>}
                        {hasPermission('integration.manage') && (r.status === 'active'
                          ? <Tooltip title="Pause"><IconButton size="small" onClick={() => pause.mutate(r.id)}><Pause fontSize="small" /></IconButton></Tooltip>
                          : <Tooltip title="Activate"><IconButton size="small" color="success" onClick={() => activate.mutate(r.id)}><PlayArrow fontSize="small" /></IconButton></Tooltip>)}
                        {hasPermission('integration.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove.mutate(r.id)}><Delete fontSize="small" /></IconButton></Tooltip>}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {createOpen && <CreateDialog connectors={connectors ?? []} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); invalidate(); }} />}
      {credFor && <CredentialDialog integration={credFor} onClose={() => setCredFor(null)} />}
      {oauthFor && <OAuthDialog integration={oauthFor} onClose={() => setOauthFor(null)} />}
      {runFor && <RunDialog integration={runFor} onClose={() => setRunFor(null)} />}
    </>
  );
}

function RunDialog({ integration, onClose }: { integration: Integration; onClose: () => void }) {
  const [action, setAction] = useState('track');
  const [payload, setPayload] = useState('{}');
  const [csvName, setCsvName] = useState('import.csv');
  const [csv, setCsv] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const invoke = useMutation({
    mutationFn: () => integrationApi.invoke(integration.id, action, JSON.parse(payload || '{}')),
    onSuccess: (r) => setResult(JSON.stringify(r, null, 2)),
    onError: (e: Error) => setResult(`Error: ${e.message}`),
  });
  const importCsv = useMutation({
    mutationFn: () => (integration.transport === 'sftp' ? integrationApi.sftpPull(integration.id) : integrationApi.importCsv(integration.id, csvName, csv)),
    onSuccess: (r) => setResult(`Imported ${r.rowCount} row(s).\n${JSON.stringify(r.preview, null, 2)}`),
    onError: (e: Error) => setResult(`Error: ${e.message}`),
  });

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Run / Import — {integration.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info">Invoke a connector action or import a CSV ({integration.transport === 'sftp' ? 'SFTP pull uses the configured source' : 'paste CSV content'}).</Alert>
          <Stack direction="row" spacing={1}>
            <TextField label="Action" size="small" value={action} onChange={(e) => setAction(e.target.value)} sx={{ width: 180 }} />
            <TextField label="Payload (JSON)" size="small" fullWidth value={payload} onChange={(e) => setPayload(e.target.value)} />
            <Button variant="outlined" onClick={() => invoke.mutate()}>Invoke</Button>
          </Stack>
          {integration.transport !== 'sftp' && (
            <>
              <TextField label="File name" size="small" value={csvName} onChange={(e) => setCsvName(e.target.value)} />
              <TextField label="CSV content" multiline minRows={3} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder="col1,col2&#10;a,b" />
            </>
          )}
          <Button variant="outlined" onClick={() => importCsv.mutate()}>{integration.transport === 'sftp' ? 'SFTP Pull' : 'Import CSV'}</Button>
          {result && <TextField label="Result" multiline minRows={4} value={result} InputProps={{ readOnly: true }} />}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function OAuthDialog({ integration, onClose }: { integration: Integration; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ clientId: '', clientSecret: '', tokenUrl: '', grantType: 'client_credentials', scopes: '' });
  const [msg, setMsg] = useState<string | null>(null);
  const status = useQuery({ queryKey: ['oauth-status', integration.id], queryFn: () => oauthApi.status(integration.id) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['oauth-status', integration.id] });
  const save = useMutation({
    mutationFn: () => oauthApi.config(integration.id, { clientId: form.clientId, clientSecret: form.clientSecret, tokenUrl: form.tokenUrl, grantType: form.grantType, scopes: form.scopes.split(',').map((s) => s.trim()).filter(Boolean) }),
    onSuccess: () => setMsg('OAuth2 client saved.'),
  });
  const getToken = useMutation({ mutationFn: () => oauthApi.token(integration.id), onSuccess: () => { setMsg('Access token acquired.'); refresh(); } });
  const doRefresh = useMutation({ mutationFn: () => oauthApi.refresh(integration.id), onSuccess: () => { setMsg('Token refreshed.'); refresh(); } });

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>OAuth2 — {integration.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {msg && <Alert severity="success" onClose={() => setMsg(null)}>{msg}</Alert>}
          {status.data ? (
            <Alert severity="info">
              Token type {status.data.token_type}; expires {status.data.expires_at ? new Date(Number(status.data.expires_at)).toLocaleString() : 'n/a'}; refresh token {status.data.has_refresh_token ? 'present' : 'absent'}.
            </Alert>
          ) : <Alert severity="warning">No token yet. Configure the client and acquire one.</Alert>}
          <TextField label="Client ID" value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))} />
          <TextField label="Client Secret" type="password" value={form.clientSecret} onChange={(e) => setForm((f) => ({ ...f, clientSecret: e.target.value }))} />
          <TextField label="Token URL" value={form.tokenUrl} onChange={(e) => setForm((f) => ({ ...f, tokenUrl: e.target.value }))} />
          <TextField select label="Grant type" value={form.grantType} onChange={(e) => setForm((f) => ({ ...f, grantType: e.target.value }))}>
            <MenuItem value="client_credentials">client_credentials</MenuItem>
            <MenuItem value="authorization_code">authorization_code</MenuItem>
          </TextField>
          <TextField label="Scopes (comma-separated)" value={form.scopes} onChange={(e) => setForm((f) => ({ ...f, scopes: e.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button disabled={!form.clientId || !form.clientSecret || !form.tokenUrl || save.isPending} onClick={() => save.mutate()}>Save client</Button>
        <Button variant="outlined" onClick={() => getToken.mutate()}>Get token</Button>
        <Button variant="contained" onClick={() => doRefresh.mutate()}>Refresh</Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateDialog({ connectors, onClose, onCreated }: { connectors: ConnectorMeta[]; onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState<CreateIntegrationInput>({ name: '', category: 'custom', connectorKey: 'generic_rest', direction: 'outbound', transport: 'rest', authType: 'none', baseUrl: '' });
  const connector = connectors.find((c) => c.key === form.connectorKey);
  const create = useMutation({ mutationFn: () => integrationApi.create({ ...form, baseUrl: form.baseUrl || null }), onSuccess: onCreated });

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Integration</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField select label="Connector" value={form.connectorKey} onChange={(e) => {
            const c = connectors.find((x) => x.key === e.target.value);
            setForm((f) => ({ ...f, connectorKey: e.target.value, category: c?.category ?? f.category, authType: c?.authTypes[0] ?? 'none', transport: c?.transports[0] ?? 'rest' }));
          }}>
            {connectors.map((c) => <MenuItem key={c.key} value={c.key}>{c.label} — {c.category.toUpperCase()}</MenuItem>)}
          </TextField>
          {connector && <Alert severity="info">{connector.description}</Alert>}
          <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Stack direction="row" spacing={2}>
            <TextField select fullWidth label="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as CreateIntegrationInput['category'] }))}>
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Direction" value={form.direction} onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value as CreateIntegrationInput['direction'] }))}>
              {DIRECTIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Auth" value={form.authType} onChange={(e) => setForm((f) => ({ ...f, authType: e.target.value as CreateIntegrationInput['authType'] }))}>
              {AUTH_TYPES.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="Base URL (optional)" value={form.baseUrl ?? ''} onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!form.name || create.isPending} onClick={() => create.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function CredentialDialog({ integration, onClose }: { integration: Integration; onClose: () => void }) {
  const [kind, setKind] = useState<SecretKind>(integration.auth_type === 'none' ? 'api_key' : (integration.auth_type as SecretKind));
  const [secret, setSecret] = useState('');
  const save = useMutation({ mutationFn: () => integrationApi.setCredential(integration.id, kind, secret), onSuccess: onClose });

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={onClose}>
      <DialogTitle>Credentials — {integration.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="warning">Secrets are encrypted at rest (AES-256-GCM) and never displayed again.</Alert>
          <TextField select label="Kind" value={kind} onChange={(e) => setKind(e.target.value as SecretKind)}>
            {(['api_key', 'oauth2', 'basic', 'hmac', 'sftp'] as SecretKind[]).map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
          </TextField>
          <TextField label="Secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!secret || save.isPending} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
